import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SheetFooter } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import type {
  AppToolWrite,
  ConfirmAppointmentAppTool,
} from '@/features/tools/types'

// Deliberately writes only instructions; the general builder edits tool wiring
// and agent flags that are not part of this confirmation configuration surface.
export function ConfirmationInstructionsForm({
  tool,
  onSubmit,
  onCancel,
}: {
  tool: ConfirmAppointmentAppTool
  onSubmit: (value: Partial<AppToolWrite>) => Promise<void>
  onCancel?: () => void
}) {
  const [instructions, setInstructions] = useState(
    tool.angelica_instructions ?? ''
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={async (event) => {
        event.preventDefault()
        if (saving || !instructions.trim()) return
        setSaving(true)
        setError(null)
        try {
          await onSubmit({ angelica_instructions: instructions.trim() })
        } catch (saveError) {
          setError(
            saveError instanceof Error
              ? saveError.message
              : 'Unable to save instructions'
          )
        } finally {
          setSaving(false)
        }
      }}>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        <p className="text-sm text-muted-foreground">
          Confirms attendance for an appointment. For reminder calls, Angelica
          confirms the appointment being discussed. For other calls, she needs
          the patient's appointment details to confirm the right visit. Changes
          to this shared tool apply to every organization that uses it. Use a
          duty override to customize instructions for one duty.
        </p>
        <div className="space-y-2">
          <Label htmlFor="confirmation-instructions">Instructions</Label>
          <Textarea
            id="confirmation-instructions"
            rows={3}
            disabled={saving}
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <SheetFooter className="shrink-0 flex-row justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={saving || !instructions.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </SheetFooter>
    </form>
  )
}
